import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
  MuiModal,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../components";
import { PAGE_IDS, TIMEZONE_STANDARD_CODES } from "../../helpers/constants";
import { useSiteCore } from "../../hooks/useSiteCore";
import { CancelFieldModal } from "../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { CreateSystemMessageSchema } from "./modelsAndSchemas/ValidationSchema";

import { createSystemMessageSiteCoreKeys } from "./siteCore/SiteCoreKeys";

import { toggleLoader } from "../../redux/slices/appState/appStateSlice";

import { useToast } from "../../hooks/useToast";
import {
  convertUTCToTimeZone,
  covertSelectedTimezoneWithUtc,
  getValueOrBlank,
  isNotEmpty,
} from "../../helpers/utils";

import { useGetUserTimeZoneListForAdminUserQuery } from "../../api/services/manageUser/manageUserApi";

import { CreateSystemMessageWrapper } from "./CreateSystemMessage.style";
import { CreateSystemMessageForm } from "./components/createSystemMessageForm/CreateSystemMessageForm";
import { UserCountry } from "../systemMessageSearch/modelsAndSchemas/models";
import {
  createSystemMessageFormDefaultValues,
  SystemCountryLanguageCode,
  TimeZoneType,
} from "./modelsAndSchemas/models";
import {
  useCreateSystemMessageMutation,
  useEditSystemMessageMutation,
  useGetSystemMessageDetailsQuery,
} from "../../api/services/systemMessage/SystemMessageApi";
import { CountryLanguageCodes } from "../../api/services/systemMessage/models";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";

export const pageId = PAGE_IDS.CREATE_SYSTEM_MESSAGE;

interface CreateSystemMessageProps {
  open: boolean;
  onClose: () => void;
  handleSuccess: () => void;
  isEditMessage?: boolean;
  selectedMessageId?: string;
  countryList: UserCountry[];
  setShowCreateSuccessModal: (value: boolean) => void;
  userCountryDateFormatWithTime: string;
}

export const CreateSystemMessage: FC<CreateSystemMessageProps> = ({
  open,
  onClose,
  handleSuccess,
  selectedMessageId = "",
  isEditMessage = false,
  countryList,
  setShowCreateSuccessModal,
  userCountryDateFormatWithTime,
}) => {
  const { loggedInUser } = useGetLoggedInUserDetails();

  const dispatch = useDispatch();
  const {
    commonValidationMessages,
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
  } = useSiteCore();
  const { displayToast } = useToast();
  const [showCancelFieldModal, setShowCancelFieldModal] = useState(false);
  const [showEditConfirmationModal, setShowEditConfirmationModal] =
    useState(false);
  const [isFormReset, setIsFormReset] = useState(false);

  const {
    isPending: isCreateMessageLoading,
    isError: isCreateMessageError,
    mutateAsync: createMessageMutation,
    isSuccess: isCreateMessageSuccess,
  } = useCreateSystemMessageMutation();

  const {
    isPending: isEditMessageLoading,
    isError: isEditMessageError,
    mutateAsync: editMessageMutation,
    isSuccess: isEditMessageSuccess,
  } = useEditSystemMessageMutation();

  const {
    data: userTimeZoneList,
    isLoading: isUserTimeZoneListLoading,
    isError: isUserTimeZoneListError,
  } = useGetUserTimeZoneListForAdminUserQuery();

  const {
    data: messageData,
    isFetching: isSystemMessageDetailsLoading,
    isError: isSystemMessageDetailsError,
    refetchWithParams: refetchSystemMessageDetails,
  } = useGetSystemMessageDetailsQuery();

  const getMessageLabel = (key: string) =>
    fetchLabel(createSystemMessageSiteCoreKeys.createMessageKey, key);

  const methods = useForm({
    mode: "all",
    defaultValues: createSystemMessageFormDefaultValues,
    resolver: yupResolver(
      CreateSystemMessageSchema(
        {
          ...commonValidationMessages,
          toDateTimeFromDateValidationMessage: getMessageLabel(
            createSystemMessageSiteCoreKeys.toDateTimeFromDateValidationMessage,
          ),
          invalidLinkURL: getMessageLabel(
            createSystemMessageSiteCoreKeys.invalidLinkURL,
          ),
          enterValidFromDateTime: getMessageLabel(
            createSystemMessageSiteCoreKeys.enterValidFromDateTime,
          ),
        },
        userCountryDateFormatWithTime,
      ),
    ),
  });

  const {
    formState: { isDirty },
    handleSubmit,
    getValues,
    reset,
  } = methods;

  useEffect(() => {
    if (isEditMessage && selectedMessageId) {
      refetchSystemMessageDetails({ id: selectedMessageId });
    }
  }, [isEditMessage, selectedMessageId]);

  useEffect(() => {
    if (isNotEmpty(messageData) && isEditMessage) {
      reset({
        ...createSystemMessageFormDefaultValues,
        messageTitle: messageData.messageTitle,
        messageDescription: messageData.messageDescription,
        countryAndLanguage: [messageData.languageCode],
        persistence: messageData.isPersistent,
        timeZone: messageData.timezone,
        addLinkInformation: messageData.linkToMessage,
        ...(messageData.linkToMessage
          ? {
              linkName: messageData.linkName,
              linkUrl: messageData.linkUrl,
              linkPosition: messageData.linkPosition,
            }
          : {}),
        fromDate: convertUTCToTimeZone(
          messageData.fromDate,
          messageData.timezone,
          userCountryDateFormatWithTime,
        ),
        toDate: convertUTCToTimeZone(
          messageData.toDate,
          messageData.timezone,
          userCountryDateFormatWithTime,
        ),
      });
      setIsFormReset(true);
    }
  }, [selectedMessageId, messageData]);

  useEffect(() => {
    if (
      isUserTimeZoneListError ||
      isCreateMessageError ||
      isEditMessageError ||
      isSystemMessageDetailsError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    }
  }, [
    isUserTimeZoneListError,
    isCreateMessageError,
    isEditMessageError,
    isSystemMessageDetailsError,
  ]);

  const successModalHandler = () => {
    onClose();
    handleSuccess();
  };

  useEffect(() => {
    if (isCreateMessageSuccess) {
      setShowCreateSuccessModal(true);
      successModalHandler();
    }
  }, [isCreateMessageSuccess]);

  useEffect(() => {
    if (isEditMessageSuccess) {
      displayToast(
        getMessageLabel(
          createSystemMessageSiteCoreKeys.systemMessageUpdatedMessage,
        ),
      );
      handleSuccess();
    }
  }, [isEditMessageSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        createUser: {
          isVisible:
            isUserTimeZoneListLoading ||
            isCreateMessageLoading ||
            isEditMessageLoading ||
            isSystemMessageDetailsLoading,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    };
  }, [
    isUserTimeZoneListLoading,
    isCreateMessageLoading,
    isEditMessageLoading,
    isSystemMessageDetailsLoading,
  ]);

  const getHeaderFooterLabel = (key: string) =>
    fetchLabel(createSystemMessageSiteCoreKeys.headerKey, key);

  const timeZoneListOptions = useMemo(() => {
    let timeZoneList: OptionsDataType[] = [];
    if (isNotEmpty(userTimeZoneList)) {
      userTimeZoneList?.forEach((item: TimeZoneType) => {
        let timezone = {} as OptionsDataType;
        timezone.label = item.title;
        timezone.value = item.id;
        item?.standardCodes?.forEach((subItem) => {
          if (subItem?.itemCodeStandardName === TIMEZONE_STANDARD_CODES.IANA) {
            timezone.value = subItem?.standardCodeValue;
            timeZoneList.push(timezone);
          }
        });
      });
    }
    return timeZoneList;
  }, [userTimeZoneList]);

  const countryListOptions = useMemo(() => {
    let countries: SystemCountryLanguageCode[] = [];
    if (isNotEmpty(countryList)) {
      countries = countryList.map((country: UserCountry, key: number) => ({
        label: country.masterCountryName,
        value: key as unknown as string,
        additionalInfo: country.localeDescription,
        languageCode: country.localeName,
        countryCode: country.countryIdentifier,
      }));
    }
    return countries;
  }, [countryList]);

  const handleModalClose = () => {
    if (!isDirty) return onClose();
    setShowCancelFieldModal(true);
  };

  const prepareApiData = () => {
    const {
      messageTitle,
      messageDescription,
      fromDate,
      toDate,
      countryAndLanguage,
      persistence,
      timeZone,
      addLinkInformation,
      linkName,
      linkUrl,
      linkPosition,
    } = getValues();

    const countryLanguageCodes: CountryLanguageCodes[] = countryListOptions
      .filter((item: SystemCountryLanguageCode) =>
        (countryAndLanguage ?? []).includes(item.value),
      )
      .map((item: SystemCountryLanguageCode) => ({
        languageCode: String(item.languageCode),
        countryCode: item.countryCode,
      }));

    const linkData = addLinkInformation
      ? {
          LinkName: linkName,
          LinkUrl: linkUrl,
          LinkPosition: linkPosition,
        }
      : {};

    return {
      messageTitle,
      messageDescription,
      fromDate: covertSelectedTimezoneWithUtc(
        timeZone,
        fromDate as string,
        userCountryDateFormatWithTime,
      ),
      toDate: covertSelectedTimezoneWithUtc(
        timeZone,
        toDate as string,
        userCountryDateFormatWithTime,
      ),
      sentBy: getValueOrBlank(loggedInUser?.id),
      countryLanguageCodes,
      isPersistent: Boolean(persistence),
      timezone: timeZone,
      LinkToMessage: Boolean(addLinkInformation),
      ...linkData,
    };
  };

  const handleCreateUserApi = () => {
    createMessageMutation(prepareApiData());
  };

  const handleEditConfirmationModal = () => {
    editMessageMutation({
      ...prepareApiData(),
      id: getValueOrBlank(selectedMessageId),
      countryLanguageCodes: [
        {
          languageCode: messageData?.languageCode,
          countryCode: messageData?.countryCodes,
        },
      ],
    });
    setShowEditConfirmationModal(false);
  };

  const handleFinalSubmit = () =>
    isEditMessage ? setShowEditConfirmationModal(true) : handleCreateUserApi();

  const modalTitle = useMemo(() => {
    if (isEditMessage) {
      return `${getHeaderFooterLabel(createSystemMessageSiteCoreKeys.editSystemMessagesHeader)} - [${getValueOrBlank(messageData?.messageIdFormat)}]`;
    }
    return fetchComponentTitle(
      createSystemMessageSiteCoreKeys.createMessageKey,
    );
  }, [isEditMessage, messageData]);

  return (
    <MuiModal
      onClose={handleModalClose}
      title={modalTitle}
      open={open}
      primaryButtonTitle={
        isEditMessage
          ? commonLabels.update
          : getHeaderFooterLabel(createSystemMessageSiteCoreKeys.publishButton)
      }
      onPrimaryButtonClick={handleSubmit(handleFinalSubmit)}
      onSecondaryButtonClick={handleModalClose}
      secondaryButtonTitle={commonLabels.cancel}
      hasLargeContent
    >
      <CreateSystemMessageWrapper>
        <FormProvider {...methods}>
          <CreateSystemMessageForm
            pageId={pageId}
            timeZoneListOptions={timeZoneListOptions}
            countryListOptions={countryListOptions}
            isEditMessage={isEditMessage}
            messageData={messageData}
            isFormReset={isFormReset}
            setIsFormReset={setIsFormReset}
            userCountryDateFormatWithTime={userCountryDateFormatWithTime}
          />
        </FormProvider>

        {showCancelFieldModal && (
          <CancelFieldModal
            showModal={showCancelFieldModal}
            handleModalClose={() => setShowCancelFieldModal(false)}
            handlePrimaryButtonClick={onClose}
            cancelMessage={commonLabels.closeButtonActionText}
          />
        )}

        {showEditConfirmationModal && (
          <WarningConfirmationModal
            show={showEditConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={() => setShowEditConfirmationModal(false)}
            onPrimaryButtonClick={handleEditConfirmationModal}
            labelContent={getMessageLabel(
              createSystemMessageSiteCoreKeys.systemMessageUpdateConfirmation,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </CreateSystemMessageWrapper>
    </MuiModal>
  );
};
